// Werbe-Mitteilungen zu Aktionen (docs/integration/bestell-app.md §17): Wer eine Aktion anlegt oder freigibt, kann sie
// im angebundenen Vertriebskanal (Bestell-App) ankündigen – oder später zu einer bestehenden Aktion. Merxo sendet
// selbst nichts: Der Kanal holt bereite Mitteilungen ab, sendet sie an Personen mit Einwilligung und meldet das
// Ergebnis. Merxo verlässt sich nicht auf die Pflichten des Kanals, sondern zeigt die gemeldeten Zahlen an.
//
//   geplant (scheduled) → bereit (ready) → abgeholt (fetched) → gesendet (sent) | fehlgeschlagen (failed)
//   geplant | bereit → abgebrochen (cancelled)  ·  bereit → fehlgeschlagen (bis zum Ende der Aktion nicht abgeholt)
import { defineContract, defineEvent } from '@shared/contract'
import { zId } from '@shared/ids'
import { listInput, listOutput } from '@shared/list'
import { z } from 'zod'
import { zById, zOptionalText, zPerson } from './common'
import { brand } from '@shared/brand'

const READ = 'pricing.campaign.read'
const WRITE = 'pricing.campaign.write'
/** nur der technische Zugang des Vertriebskanals */
const DELIVER = 'pricing.campaign.deliver'

export const CAMPAIGN_CHANNELS = ['bestell-app'] as const
export type CampaignChannel = (typeof CAMPAIGN_CHANNELS)[number]

export const CAMPAIGN_STATES = ['scheduled', 'ready', 'fetched', 'sent', 'cancelled', 'failed'] as const
export type CampaignState = (typeof CAMPAIGN_STATES)[number]
/** noch nicht abgeschlossen */
export const CAMPAIGN_OPEN_STATES = ['scheduled', 'ready', 'fetched'] as const satisfies readonly CampaignState[]

export const CAMPAIGN_AUDIENCES = ['private', 'business', 'all'] as const
export type CampaignAudience = (typeof CAMPAIGN_AUDIENCES)[number]

export const CAMPAIGN_TARGET_KINDS = ['offers', 'group', 'item'] as const
export type CampaignTargetKind = (typeof CAMPAIGN_TARGET_KINDS)[number]

/** Grenzen der Mitteilung auf dem Sperrbildschirm des iPhones */
export const CAMPAIGN_TITLE_MAX = 60
export const CAMPAIGN_BODY_MAX = 178

export const zCampaignTitle = z
  .string({ error: 'Bitte eine Überschrift angeben.' })
  .trim()
  .min(1, { error: 'Bitte eine Überschrift angeben.' })
  .max(CAMPAIGN_TITLE_MAX, { error: `Die Überschrift darf höchstens ${CAMPAIGN_TITLE_MAX} Zeichen haben.` })
  .regex(/^[^\r\n]*$/, { error: 'Die Überschrift ist einzeilig.' })

export const zCampaignBody = z
  .string({ error: 'Bitte einen Text angeben.' })
  .trim()
  .min(1, { error: 'Bitte einen Text angeben.' })
  .max(CAMPAIGN_BODY_MAX, { error: `Der Text darf höchstens ${CAMPAIGN_BODY_MAX} Zeichen haben.` })

/** Was der Tipp auf die Mitteilung in der App öffnet: die Angebote, eine Warengruppe oder einen Artikel. */
export const zCampaignTarget = z.discriminatedUnion('kind', [
  z.strictObject({ kind: z.literal('offers') }),
  z.strictObject({ kind: z.literal('group'), groupId: zId }),
  z.strictObject({ kind: z.literal('item'), itemId: zId }),
])
export type CampaignTarget = z.output<typeof zCampaignTarget>

/** Zeitpunkt nach ISO 8601 (mit Zeitzone). */
const zInstant = z.iso.datetime({ offset: true, error: 'Bitte einen Zeitpunkt nach ISO 8601 angeben.' })

/**
 * Die Ankündigung, wie sie an einer Aktion mitgegeben wird (promotion.create | approve | adopt) – ohne Angabe von
 * Überschrift und Text belegt Merxo beides aus Aktion und Zeitraum vor.
 */
export const zCampaignAnnounce = z.strictObject({
  channel: z.enum(CAMPAIGN_CHANNELS).default('bestell-app'),
  /** Überschrift, höchstens 60 Zeichen; ohne Angabe der Name der Aktion */
  title: zCampaignTitle.optional(),
  /** Text, höchstens 178 Zeichen; ohne Angabe aus Artikeln und Zeitraum der Aktion */
  body: zCampaignBody.optional(),
  target: zCampaignTarget.default({ kind: 'offers' }),
  /** ohne Angabe nach der Preisgruppe der Aktion: brutto → Privatkunden, netto → Geschäftskunden */
  audience: z.enum(CAMPAIGN_AUDIENCES).optional(),
  /** frühester Versand; ohne Angabe sofort (bei einer Aktion, die auf Freigabe wartet: mit der Freigabe) */
  sendAt: zInstant.optional(),
})
export type CampaignAnnounce = z.input<typeof zCampaignAnnounce>

/** Kurzform am Ergebnis der Aktionen, die eine Ankündigung mitnehmen */
export const zCampaignBrief = z.object({ id: zId, state: z.enum(CAMPAIGN_STATES), sendAt: z.date(), message: z.string() })

export const zCampaignTargetView = z.object({
  kind: z.enum(CAMPAIGN_TARGET_KINDS),
  /** Warengruppe bzw. Artikel; null bei den Angeboten */
  id: zId.nullable(),
  /** „Angebote“, „Bier“, „Augustiner Lagerbier Hell“ */
  label: z.string(),
})

export const zCampaignResult = z.object({
  /** Personen, an die gesendet wurde */
  sent: z.int(),
  /** übersprungen, weil die Einwilligung „Angebote und Aktionen“ fehlt */
  skippedNoConsent: z.int(),
  /** übersprungen wegen der Begrenzung (eine je Tag, drei in sieben Tagen) */
  skippedLimit: z.int(),
  /** Personen, bei denen jede Zustellung scheiterte */
  failed: z.int(),
  /** übersprungen, weil die Bestätigung „mindestens 18 Jahre“ fehlt (nur bei Mitteilungen mit Altersgrenze; sonst 0) */
  skippedAge: z.int(),
  /** Zeitpunkt des Versands laut Kanal */
  sentAt: z.date().nullable(),
})

export const zCampaignRow = z.object({
  id: zId,
  channel: z.enum(CAMPAIGN_CHANNELS),
  state: z.enum(CAMPAIGN_STATES),
  title: z.string(),
  body: z.string(),
  target: zCampaignTargetView,
  audience: z.enum(CAMPAIGN_AUDIENCES),
  /** höchste Altersgrenze der Artikel der Aktion (16 oder 18); null = keine. Mit Altersgrenze sendet der Kanal nur an Personen mit Volljährigkeits-Bestätigung (§21.1) */
  minAge: z.union([z.literal(16), z.literal(18)]).nullable(),
  /** `approved`: die Aktion ist freigegeben – sonst wartet die Mitteilung auf deren Freigabe */
  promotion: z.object({ id: zId, name: z.string(), period: z.string(), validFrom: z.string(), validTo: z.string(), approved: z.boolean() }),
  /** „sofort“ gewählt */
  immediate: z.boolean(),
  /** frühester Versand */
  sendAt: z.date(),
  /** danach sendet der Kanal nicht mehr (Ende des letzten Aktionstags) */
  expiresAt: z.date(),
  /** wann der Kanal frühestens sendet: 9 Uhr, wenn der Zeitpunkt in der Ruhezeit liegt; null = abgeschlossen oder die Aktion wartet auf Freigabe */
  expectedAt: z.date().nullable(),
  releasedAt: z.date().nullable(),
  claimedAt: z.date().nullable(),
  finishedAt: z.date().nullable(),
  /** Ergebnis laut Kanal; null, solange keines gemeldet ist */
  result: zCampaignResult.nullable(),
  /** Satz des Kanals bzw. Grund des Abbruchs oder Fehlschlags */
  note: z.string().nullable(),
  createdBy: zPerson,
  createdAt: z.date(),
  cancelledBy: zPerson.nullable(),
})
export type CampaignRow = z.output<typeof zCampaignRow>

export const campaignList = defineContract({
  name: 'pricing.campaign.list',
  title: 'Werbe-Mitteilungen anzeigen',
  description: 'Angekündigte Aktionen im Vertriebskanal mit Zeitpunkt, Zustand und gemeldetem Ergebnis.',
  llm: {
    description:
      'Listet Werbe-Mitteilungen (Ankündigungen von Aktionen in der Bestell-App): Überschrift, Aktion, Zeitpunkt, ' +
      'Zielgruppe, Zustand (scheduled = geplant, ready = bereit, fetched = von der App übernommen, sent = gesendet, ' +
      'cancelled = abgebrochen, failed = fehlgeschlagen) und das Ergebnis, das die App gemeldet hat (gesendet an, ' +
      'ohne Einwilligung, wegen Begrenzung übersprungen, Fehler). Filter `state`, `open` (noch nicht abgeschlossen), `promotionId`.',
    keywords: ['Werbe-Mitteilung', 'Push', 'Mitteilung', 'Ankündigung', 'App', 'Benachrichtigung', 'Kampagne'],
  },
  input: listInput({
    sortBy: ['sendAt', 'createdAt'],
    defaultSort: { by: 'sendAt', dir: 'desc' },
    filters: {
      state: z.enum(CAMPAIGN_STATES).optional(),
      /** nur geplante, bereite und abgeholte */
      open: z.boolean().optional(),
      promotionId: zId.optional(),
    },
  }),
  output: listOutput(zCampaignRow),
  permission: READ,
  risk: 'read',
})

export const campaignGet = defineContract({
  name: 'pricing.campaign.get',
  title: 'Werbe-Mitteilung öffnen',
  description: 'Eine Werbe-Mitteilung mit Text, Ziel, Zielgruppe, Zeitpunkt, Zustand und gemeldetem Ergebnis.',
  llm: {
    description:
      'Liefert eine Werbe-Mitteilung mit allen Angaben und dem Ergebnis der Bestell-App – und ob die Person sie noch ' +
      'abbrechen darf (`canCancel`, nur geplant oder bereit). Liste: pricing.campaign.list.',
    keywords: ['Werbe-Mitteilung', 'Push', 'Ergebnis', 'Ankündigung'],
  },
  input: zById,
  output: zCampaignRow.extend({ canCancel: z.boolean() }),
  permission: READ,
  risk: 'read',
})

export const campaignChannels = defineContract({
  name: 'pricing.campaign.channels',
  title: 'Vertriebskanäle für Werbe-Mitteilungen',
  description: 'Ob ein Vertriebskanal angebunden ist, der Werbe-Mitteilungen abholt – mit seinen Regeln für den Versand.',
  llm: {
    description:
      'Sagt, ob die Bestell-App angebunden ist und Werbe-Mitteilungen abholt (`connected`), und nennt die Regeln, die ' +
      'sie beim Versand einhält: Ruhezeit 20 bis 9 Uhr, höchstens eine Mitteilung je Person und Tag und drei in sieben ' +
      'Tagen, nur mit Einwilligung, Überschrift bis 60 und Text bis 178 Zeichen.',
    keywords: ['Bestell-App', 'Push', 'Werbe-Mitteilung', 'angebunden'],
  },
  input: z.strictObject({}),
  output: z.object({
    channels: z.array(
      z.object({
        key: z.enum(CAMPAIGN_CHANNELS),
        label: z.string(),
        /** ein technischer Zugang darf Werbe-Mitteilungen abholen */
        connected: z.boolean(),
        titleMax: z.int(),
        bodyMax: z.int(),
        /** Ruhezeit des Kanals, Europe/Berlin: keine Sendung von … bis … */
        quietHours: z.object({ from: z.string(), to: z.string() }),
        perDay: z.int(),
        perWeek: z.int(),
      }),
    ),
  }),
  permission: READ,
  risk: 'read',
})

export const campaignCreate = defineContract({
  name: 'pricing.campaign.create',
  title: 'Aktion im Vertriebskanal ankündigen',
  description: 'Legt eine Werbe-Mitteilung zu einer Aktion an – sofort oder zu einem Zeitpunkt. Der Kanal sendet nur an Personen mit Einwilligung.',
  llm: {
    description:
      'Kündigt eine bestehende Aktion (`promotionId`, aus pricing.promotion.list) in der Bestell-App als ' +
      `Werbe-Mitteilung an. Überschrift (bis 60 Zeichen) und Text (bis 178 Zeichen) belegt ${brand.name} ohne Angabe aus Aktion ` +
      'und Zeitraum vor. `target`: was der Tipp öffnet – die Angebote (Standard), eine Warengruppe oder einen Artikel. ' +
      '`audience`: private, business oder all (Standard nach der Preisgruppe der Aktion). `sendAt` (ISO 8601) plant den ' +
      'Versand, ohne Angabe sofort. Die App sendet nicht zwischen 20 und 9 Uhr, höchstens eine Mitteilung je Person und ' +
      'Tag und drei in sieben Tagen, nur mit Einwilligung. Eine neue Aktion samt Ankündigung: pricing.promotion.create ' +
      'mit `announce`. Abbrechen: pricing.campaign.cancel.',
    keywords: ['Push', 'Werbe-Mitteilung', 'ankündigen', 'in der App bewerben', 'Benachrichtigung', 'Angebot bewerben', 'Kampagne'],
  },
  input: zCampaignAnnounce.extend({ promotionId: zId }),
  output: zCampaignBrief.extend({ expiresAt: z.date(), title: z.string(), body: z.string() }),
  permission: WRITE,
  risk: 'write',
  effects: { external: true },
})

export const campaignCancel = defineContract({
  name: 'pricing.campaign.cancel',
  title: 'Werbe-Mitteilung abbrechen',
  description: 'Bricht eine geplante oder bereite Werbe-Mitteilung ab – solange der Vertriebskanal sie nicht übernommen hat.',
  llm: {
    description:
      'Bricht eine Werbe-Mitteilung ab, solange sie geplant oder bereit ist. Hat die Bestell-App sie schon übernommen ' +
      '(fetched), versendet sie sie bereits – dann geht kein Abbruch mehr. Die App erfährt den Abbruch sofort.',
    keywords: ['Push abbrechen', 'Werbe-Mitteilung stoppen', 'Ankündigung zurücknehmen'],
  },
  input: z.strictObject({ id: zId, reason: zOptionalText(300) }),
  output: z.object({ id: zId, state: z.enum(CAMPAIGN_STATES), message: z.string() }),
  permission: WRITE,
  risk: 'write',
})

// ---- für den Vertriebskanal (über sales.channel.claimCampaign / reportCampaign, nie direkt) ----------------------

export const campaignClaim = defineContract({
  name: 'pricing.campaign.claim',
  title: 'Werbe-Mitteilung zum Versand übernehmen',
  description: 'Der Vertriebskanal übernimmt eine bereite Werbe-Mitteilung unmittelbar vor dem Versand.',
  llm: {
    description: 'Nur für den technischen Zugang des Vertriebskanals (über sales.channel.claimCampaign).',
    keywords: [],
  },
  input: zById,
  output: z.object({ id: zId, state: z.enum(CAMPAIGN_STATES), replayed: z.boolean() }),
  permission: DELIVER,
  risk: 'write',
  assistant: 'never',
})

export const CAMPAIGN_OUTCOMES = ['sent', 'failed'] as const

const zCount = z.int({ error: 'Bitte eine ganze Zahl angeben.' }).min(0, { error: 'Die Zahl darf nicht negativ sein.' }).max(10_000_000)

export const zCampaignCounts = z.strictObject({
  /** Personen, an die gesendet wurde (mindestens ein Gerät hat die Mitteilung angenommen) */
  sent: zCount,
  /** Personen der Zielgruppe mit Gerät, aber ohne Einwilligung „Angebote und Aktionen“ */
  skippedNoConsent: zCount,
  /** Personen mit Einwilligung, die heute bzw. in den letzten sieben Tagen schon genug Werbe-Mitteilungen hatten */
  skippedLimit: zCount,
  /** Personen, bei denen jede Zustellung scheiterte */
  failed: zCount,
  /** Personen übersprungen, weil die Bestätigung „mindestens 18 Jahre“ fehlt (nur bei Mitteilungen mit `minAge`; additiv) */
  skippedAge: zCount.optional(),
})

export const campaignReport = defineContract({
  name: 'pricing.campaign.report',
  title: 'Ergebnis einer Werbe-Mitteilung melden',
  description: 'Der Vertriebskanal meldet, an wie viele Personen er eine übernommene Werbe-Mitteilung gesendet hat.',
  llm: {
    description: 'Nur für den technischen Zugang des Vertriebskanals (über sales.channel.reportCampaign).',
    keywords: [],
  },
  input: z.strictObject({
    id: zId,
    outcome: z.enum(CAMPAIGN_OUTCOMES),
    counts: zCampaignCounts,
    sentAt: zInstant,
    message: z.string().trim().max(300).optional(),
  }),
  output: z.object({ id: zId, state: z.enum(CAMPAIGN_STATES), replayed: z.boolean() }),
  permission: DELIVER,
  risk: 'write',
  assistant: 'never',
})

/** Jeder Zustandswechsel einer Werbe-Mitteilung – der Verkauf macht daraus sales.channel.campaignRequested. */
export const campaignChanged = defineEvent({
  name: 'pricing.campaign.changed',
  title: 'Werbe-Mitteilung geändert',
  payload: z.object({
    campaignId: zId,
    channel: z.enum(CAMPAIGN_CHANNELS),
    state: z.enum(CAMPAIGN_STATES),
    previous: z.enum(CAMPAIGN_STATES).nullable(),
  }),
})
